import { useState } from 'react';
import {
    useTheme,
    Container,
    Box,
    Link,
    InputAdornment,
    CircularProgress,
    Tooltip,
    TextField,
} from '@mui/material';
import {
    Done,
    Close,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { withPather } from 'react-pather';
import Loader from '../Loader';
import { 
    PrimaryButton,
    PasswordField,
} from '../general';
import { 
    validateEmail, 
    toTitleCase, 
} from '../../utils';

const SignUp = ({ pather }) => {

    const theme = useTheme();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const currentUser = useSelector(state => state.user.value);
    const isUserLoading = useSelector(state => state.userLoading.value); 

    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [showConfirmMsg, setShowConfirmMsg] = useState('');

    const [usernameCheckLoading, setUsernameCheckLoading] = useState(false);
    const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
    const [emailCheckLoading, setEmailCheckLoading] = useState(false);
    const [isEmailAvailable, setIsEmailAvailable] = useState(null);

    const [user, setUser] = useState({
        username: '',
        email: '',
        password: '',
    });

    const setUserSafe = (updStateObj) => {
        setUser((prevState) => ({
            ...prevState,
            ...updStateObj,
        }));    
    }

    // Password visibility
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

    const handleClickShowPassword = () => setShowPassword(show => !show);
    const handleClickShowPasswordConfirmation = () => setShowPasswordConfirmation(show => !show);
    //

    const isInputValid = () => {
        if (!user.username || !user.email || !user.password) {
            enqueueSnackbar('Some fields are not provided', { variant: 'error' });
            return false;
        }
        else if (user.password !== passwordConfirmation) {
            enqueueSnackbar('Passwords do not match', { variant: 'error' });
            return false;
        }
        else if(!validateEmail(user.email)){
            enqueueSnackbar('Invalid email', { variant: 'error' });
            return false;
        }        
        return true;
    }

    const handleSubmit = () => {
        setLoading(true);
        if (!isInputValid()) {
            setLoading(false);
            return;
        }
        axios.post(
            pather.back.Auth.register, 
            user,    
        )
        .then(response => {
            const data = response.data;
            enqueueSnackbar(data.status, { variant: 'success' });
            setShowConfirmMsg(true);
        })
        .catch(response => {
            const data = response?.response?.data;
            const status = data?.status;
            const errors = data?.errors;

            if (errors) { 
                const first_error_msg = errors[0]?.msg;
                first_error_msg ?
                    enqueueSnackbar(first_error_msg, { variant: 'error' }) :
                    enqueueSnackbar('Invalid input!', { variant: 'error' });
            } else {
                enqueueSnackbar(status || 'Invalid input!', { variant: 'error' });
            }

            console.log(response);
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const checkUsernameAvailability = value => {
        const success = respData => {
            const is_username_available = respData?.is_available;
            setIsUsernameAvailable(is_username_available);
        }
        checkSignUpData('username', value, success, setUsernameCheckLoading);
    }

    const checkEmailAvailability = value => {
        const success = respData => {
            const is_email_available = respData?.is_available;
            const is_email_valid = !!validateEmail(value);
            setIsEmailAvailable(is_email_available && is_email_valid);
        }
        checkSignUpData('email', value, success, setEmailCheckLoading);
    }

    const checkSignUpData = (field, value, success, loaderSetter) => {
        loaderSetter(true);
        axios.get(
            pather.back.Auth.checkSignupData, 
            {
                params: {
                    [field]: value,
                }
            }
        )
        .then(response => {
            success(response.data);
        })
        .catch(response => {
            console.log(response);
        })
        .finally(() => {
            loaderSetter(false);
        });
    }

    if (isUserLoading) return <Loader/>;
    else if (currentUser?.isLogined) navigate(pather.front.dashboard);
    
    return (
        <Container sx={{minHeight: '550px', display: 'flex', alignItems: 'center'}}>
            {
                loading ? 
                <Loader/> :
                (
                    showConfirmMsg ?  // TODO: Move 'Please activate your account' page to seperate route
                    <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', mt: 4, mb: 4, }}>
                            <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                            <h2>Please activate your account clicking on a link in the email</h2>
                        </Box>
                        <Box sx={{fontSize: '24px', mt: 2, display: 'flex', justifyContent: 'center'}}>
                            Go to&nbsp;<Link href={pather.front.login} sx={theme.link}>
                                login
                            </Link>
                        </Box>  
                    </Box> :
                    <Box sx={{display: 'flex', justifyContent: 'center', flexDirection: 'column', width: '100%', mt: 4, mb: 4, }}>
                        <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                            <h1>Signup</h1>
                        </Box>
                        <Box sx={{display: 'flex', width: '100%', justifyContent: 'center'}}>
                            <Box 
                                sx={{
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    rowGap: 2, 
                                    width: {xs: '100%', sm: '70%', md: '30vw'},
                                    maxWidth: '500px',
                                }}
                            >
                                <TextFieldWithCheck 
                                    field='username'
                                    user={user} 
                                    setUserSafe={setUserSafe}
                                    checkAvailability={checkUsernameAvailability}
                                    isAvailable={isUsernameAvailable} 
                                    checkLoading={usernameCheckLoading}
                                    setIsAvailable={setIsUsernameAvailable}
                                />
                                <TextFieldWithCheck 
                                    field='email'
                                    user={user} 
                                    setUserSafe={setUserSafe}
                                    checkAvailability={checkEmailAvailability}
                                    isAvailable={isEmailAvailable} 
                                    checkLoading={emailCheckLoading}
                                    setIsAvailable={setIsEmailAvailable}
                                />
                                <PasswordField
                                    label='Password'
                                    value={user.password} 
                                    handleChange={e => setUserSafe({password: e.target.value})} 
                                    show={showPassword} 
                                    handleShow={handleClickShowPassword}
                                />
                                <PasswordField
                                    label='Confirm password'
                                    value={passwordConfirmation} 
                                    handleChange={e => setPasswordConfirmation(e.target.value)} 
                                    show={showPasswordConfirmation} 
                                    handleShow={handleClickShowPasswordConfirmation}
                                    error={passwordConfirmation && user.password !== passwordConfirmation }
                                    helperText={passwordConfirmation && user.password !== passwordConfirmation && "Passwords do not match"}
                                />
                                <PrimaryButton
                                    text="Sign up"
                                    onClick={handleSubmit}
                                />
                            </Box>
                        </Box>
                    </Box>
                )
            }
        </Container>
    );
}

const TextFieldWithCheck = props => {
    const { 
        field, 
        user, 
        setUserSafe, 
        checkAvailability, 
        isAvailable, 
        checkLoading, 
        setIsAvailable, 
    } = props;
    return (
        <TextField 
            label={toTitleCase(field)} 
            value={user[field]} 
            onChange={e => {
                const value = e.target.value;
                setUserSafe({ [field]: value });
                value ? checkAvailability(value) : setIsAvailable(null);
            }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        {
                            checkLoading ? 
                            <CircularProgress size={18}/> : 
                            <>
                                {
                                    isAvailable !== null ?
                                    <>
                                        {   
                                            isAvailable ?
                                            <Tooltip title="Available" placement="right">
                                                <Done fontSize='inherit' sx={{color: 'green', cursor: 'pointer'}} /> 
                                            </Tooltip> :
                                            <Tooltip title={field ==='username' ? "Taken" : "Taken or invalid"} placement="right">
                                                <Close fontSize='inherit' sx={{color: 'red', cursor: 'pointer'}} />
                                            </Tooltip>
                                        }
                                    </> : null
                                }
                            </>
                        }
                    </InputAdornment>
                )
            }}
        />
    );
}

export default withPather()(SignUp);